import React, { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Navigate, useNavigate } from "react-router";
import { toRelativeUrl, OktaAuth } from "@okta/okta-auth-js";
import ErrorBoundary from "./ErrorBoundary";
import { getOktaAuthInstance } from "./lib/okta-utils";
import { Security, LoginCallback } from "@okta/okta-react";
import ManageFactors from "./components/mfa/ManageFactors";
import MainContainer from "./components/MainContainer";
import PrivacySettings from "./components/privacysettings/PrivacySettings";
import EditPassword from "./components/editpassword/EditPassword";
import { UserProfile } from "./components/userprofile/UserProfile";
import { initialize } from "./lib/amplitude-utils";
import { GlobalBannerProvider } from "./providers/GlobalBannerContext";
import config from "./lib/config";
import { LoadingContainer } from "./components/LoadingContainer";

import "./App.css";

const oktaAuth = getOktaAuthInstance();

function App(): React.JSX.Element {
  const navigate = useNavigate();
  const restoreOriginalUri = async (
    _oktaAuth: OktaAuth,
    originalUri: string,
  ): Promise<void> => {
    navigate(toRelativeUrl(originalUri, window.location.origin));
  };

  const url = window.location.href;
  const params = new URL(url).searchParams;
  const userId = params.get("amplitudeUserId");
  const deviceId = params.get("deviceId");
  const amplitudeDeviceId = params.get("amplitude_device_id");
  const isPhrUser = params.get("isPhr") === 'true' ? true : false;

  React.useEffect(() => {
    initialize({
      apiKey: config.amplitudeApiKey,
      userId: userId,
      deviceId: amplitudeDeviceId || deviceId,
    });
  }, [amplitudeDeviceId, deviceId, userId]);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ErrorBoundary>
        <GlobalBannerProvider>
          <Suspense fallback={<LoadingContainer />}>
            <Routes>
              <Route path="/" element={<MainContainer />}>
                <Route index element={<Navigate to={"/userprofile"} state={{ isPhrUser }}/>} />
                <Route path="/userprofile" element={<UserProfile />} />
                <Route path="/managefactors" element={<ManageFactors />} />
                <Route path="/privacysettings" element={<PrivacySettings />} />
                <Route path="/editpassword" element={<EditPassword />} />
              </Route>
              <Route
                path="authz_callback"
                element={
                  <LoginCallback loadingElement={<LoadingContainer />} />
                }
              />
            </Routes>
          </Suspense>
        </GlobalBannerProvider>
      </ErrorBoundary>
    </Security>
  );
}

export default App;
